import type { ArticleWithWrittenAt } from '../lib/sanity/commonGroqs';
import { getSanityImageUrl } from '../lib/sanity/sanityClient';
import type { ArticlePageCoachFragment } from '../pages/library/a/__generated-gql-types__/[articleSlug].generated';

import { getCoachProfileUrl, getLibraryArticleUrl } from './routing';

/**
 * Generate Article structured data (JSON-LD) for an article page
 */
export function generateArticleStructuredData(
  article: ArticleWithWrittenAt,
  coach?: ArticlePageCoachFragment | null
) {
  const articleUrl = getLibraryArticleUrl(article.slug?.current ?? '');
  const imageUrl = article.featuredImage 
    ? getSanityImageUrl({
        image: article.featuredImage,
        auto: 'format',
        width: 1200,
        height: 630,
        quality: 75,
      })
    : undefined;

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title || '',
    description: article.subtitle || '',
    image: imageUrl,
    url: articleUrl,
    datePublished: article.writtenAt,
    dateModified: article.lastModifiedAt || article.writtenAt,
    author: coach ? generatePersonStructuredData(coach) : {
      '@type': 'Organization',
      name: 'Leland',
    },
    publisher: {
      '@type': 'Organization',
      name: 'Leland',
      url: 'https://leland.co',
      logo: {
        '@type': 'ImageObject',
        url: 'https://www.joinleland.com/images/leland-og.png',
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': articleUrl,
    },
  };

  // Add keywords if categories exist
  if (article.categories && article.categories.length > 0) {
    (structuredData as Record<string, unknown>).keywords = article.categories
      .map(cat => cat.title)
      .filter(Boolean)
      .join(', ');
  }

  return structuredData;
}

/**
 * Generate Person structured data (JSON-LD) for a coach
 */
export function generatePersonStructuredData(coach: ArticlePageCoachFragment) {
  const fullName = [coach.user.firstName, coach.user.lastName]
    .filter(Boolean)
    .join(' ');

  const headline = coach.categoryInformationList?.[0]?.headline || 'Career Coach';

  return {
    '@context': 'https://schema.org',
    '@type': 'Person',
    name: fullName,
    image: coach.user.pictureLink,
    jobTitle: headline,
    affiliation: {
      '@type': 'Organization',
      name: 'Leland',
      url: 'https://www.joinleland.com',
    },
    url: getCoachProfileUrl(coach.slug, coach.categoryInformationList?.[0]?.category.slug, { absolute: true }),
  };
}

/**
 * Generate ProfessionalService structured data (JSON-LD) for a coach
 */
export function generateProfessionalServiceStructuredData(coach: ArticlePageCoachFragment) {
  const fullName = [coach.user.firstName, coach.user.lastName]
    .filter(Boolean)
    .join(' ');

  return {
    '@context': 'https://schema.org',
    '@type': 'ProfessionalService',
    name: `${fullName} - Career Coaching Services`,
    provider: generatePersonStructuredData(coach),
    areaServed: {
      '@type': 'Country',
      name: 'United States',
    },
    serviceType: 'Career Coaching',
    url: getCoachProfileUrl(coach.slug, coach.categoryInformationList?.[0]?.category.slug, { absolute: true }),
  };
}

/**
 * Generate BreadcrumbList structured data (JSON-LD) for article navigation
 */
export function generateBreadcrumbStructuredData(article: ArticleWithWrittenAt) {
  const breadcrumbs = [
    {
      '@type': 'ListItem',
      position: 1,
      name: 'Home',
      item: 'https://www.joinleland.com',
    },
    {
      '@type': 'ListItem',
      position: 2,
      name: 'Library',
      item: 'https://www.joinleland.com/library',
    },
  ];

  if (article.categories && article.categories.length > 0) {
    article.categories.forEach((category) => {
      breadcrumbs.push({
        '@type': 'ListItem',
        position: breadcrumbs.length + 1,
        name: category.title || '',
        item: `https://www.joinleland.com/library/${category.slug?.current || ''}`,
      });
    });
  }

  breadcrumbs.push({
    '@type': 'ListItem',
    position: breadcrumbs.length + 1,
    name: article.title || '',
    item: getLibraryArticleUrl(article.slug?.current ?? '', { absolute: true }),
  });

  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbs,
  };
}
